"use client";

import { c as _c } from "react/compiler-runtime";
import type { api } from "@agroproj/backend/convex/_generated/api";
import type { FunctionReturnType } from "convex/server";
import type { Route } from "next";
import Link from "next/link";
import { useState } from "react";
import { Skeleton, TextLineSkeleton } from "@/components/ui/skeleton";
import { projectWorkflowStatusEnum } from "@/data/entity-types";
import { cn } from "@/lib/utils";
type ProjectsDashboard = FunctionReturnType<typeof api.projects.queries.getProjectsDashboard>;
type DashboardWorkflow = ProjectsDashboard["openByWorkflow"][number]["workflow"];
const WORKFLOW_LABELS = {
  unrecorded: "Não registrado",
  ...projectWorkflowStatusEnum
} satisfies Record<DashboardWorkflow, string>;
const workflowRowClassName = "grid grid-cols-[9.5rem_minmax(0,1fr)_2rem] items-center gap-3 sm:grid-cols-[11rem_minmax(0,1fr)_2.5rem]";

/** Tallest column in px; the plot is a little taller so the cap label fits above it. */
const COLUMN_MAX_HEIGHT = 80;

/**
 * Horizontal bars of open projects per workflow stage. Single series, so no legend;
 * every count is printed at the bar tip and the stage name links to its filtered list.
 */
export function OpenWorkflowChart(t0) {
  const $ = _c(11);
  const {
    rows,
    hrefFor
  } = t0;
  let t1;
  if ($[0] !== rows) {
    t1 = Math.max(1, ...rows.map(_temp));
    $[0] = rows;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const max = t1;
  let t2;
  if ($[2] !== hrefFor || $[3] !== max || $[4] !== rows) {
    let t3;
    if ($[6] !== hrefFor || $[7] !== max) {
      t3 = row_0 => {
        const href = hrefFor(row_0.workflow);
        const label = WORKFLOW_LABELS[row_0.workflow];
        return <li className={cn("group/bar relative", workflowRowClassName)} key={row_0.workflow}><span className="text-muted-foreground truncate">{href ? <Link className="group-hover/bar:text-foreground focus-visible:after:ring-ring/50 rounded-sm transition-colors outline-none after:absolute after:inset-0 focus-visible:after:ring-2" href={href}>{label}</Link> : label}</span><span aria-hidden="true" className="h-2"><span className="bg-chart-1 block h-full rounded-r-[4px]" style={{
              width: `${row_0.count / max * 100}%`
            }} /></span><span className="text-foreground text-right tabular-nums">{row_0.count}</span></li>;
      };
      $[6] = hrefFor;
      $[7] = max;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = rows.map(t3);
    $[2] = hrefFor;
    $[3] = max;
    $[4] = rows;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <ul className="flex flex-col gap-2 text-xs/relaxed">{t2}</ul>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

/** Loading `OpenWorkflowChart`: the same stage rows with an empty track for each bar. */
function _temp(row) {
  return row.count;
}
export function OpenWorkflowChartSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ul aria-busy="true" className="flex flex-col gap-2 text-xs/relaxed">{Object.values(WORKFLOW_LABELS).map(_temp2)}</ul>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(label) {
  return <li className={workflowRowClassName} key={label}><span className="text-muted-foreground truncate">{label}</span><Skeleton className="h-2 rounded-l-none rounded-r-[4px]" /><TextLineSkeleton className="ml-auto w-4" /></li>;
}
const monthKeyFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo",
  year: "numeric",
  month: "2-digit"
});
const monthShortFormat = new Intl.DateTimeFormat("pt-BR", {
  month: "short",
  timeZone: "UTC"
});
const monthLongFormat = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC"
});

/** The 12 São Paulo calendar months ending with the current one, oldest first. */
function lastTwelveMonths(now: number) {
  const [year, month] = monthKeyFormat.format(now).split("-").map(Number);
  return Array.from({
    length: 12
  }, (_, index) => {
    // Mid-month UTC dates keep the labels on the intended month in any zone.
    const date = Date.UTC(year ?? 1970, (month ?? 1) - 12 + index, 15);
    return {
      key: monthKeyFormat.format(date),
      short: monthShortFormat.format(date).replace(".", ""),
      long: monthLongFormat.format(date)
    };
  });
}

/** Columns of projects created per month over the last year, labeled on each non-zero cap. */
export function MonthlyProjectsChart(t0) {
  const $ = _c(19);
  const {
    months
  } = t0;
  const [now] = useState(_temp3);
  let columns;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== months || $[1] !== now) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const counts = new Map(months.map(_temp4));
      columns = lastTwelveMonths(now).map(month => ({
        ...month,
        count: counts.get(month.key) ?? 0
      }));
      const max = Math.max(...columns.map(_temp5));
      if (max === 0) {
        let t5;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <p className="text-muted-foreground">Nenhum projeto criado nos últimos 12 meses.</p>;
          $[7] = t5;
        } else {
          t5 = $[7];
        }
        t4 = t5;
        break bb0;
      }
      t3 = "text-xs/relaxed";
      t1 = "flex h-28 items-end gap-1 border-b";
      let t5;
      if ($[8] !== max) {
        t5 = month_1 => <li className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1" key={month_1.key}><span className="sr-only">{`${month_1.long}: ${month_1.count} ${month_1.count === 1 ? "projeto" : "projetos"}`}</span>{month_1.count > 0 ? <span aria-hidden="true" className="text-foreground tabular-nums">{month_1.count}</span> : null}<span aria-hidden="true" className="bg-chart-1 w-full max-w-6 rounded-t-[4px]" style={{
            height: `${Math.round(month_1.count / max * COLUMN_MAX_HEIGHT)}px`
          }} /></li>;
        $[8] = max;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      t2 = columns.map(t5);
    }
    $[0] = months;
    $[1] = now;
    $[2] = columns;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    columns = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t2) {
    t5 = <ol className={t1}>{t2}</ol>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== columns) {
    t6 = <MonthAxis months={columns} />;
    $[13] = columns;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t5 || $[17] !== t6) {
    t7 = <div className={t3}>{t5}{t6}</div>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function _temp5(month_0) {
  return month_0.count;
}
function _temp4(item) {
  return [item.month, item.count];
}
function _temp3() {
  return Date.now();
}
function MonthAxis(t0) {
  const $ = _c(4);
  const {
    months
  } = t0;
  let t1;
  if ($[0] !== months) {
    t1 = months.map(_temp6);
    $[0] = months;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ol aria-hidden="true" className="text-muted-foreground mt-1 flex gap-1 text-center">{t1}</ol>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/** Loading `MonthlyProjectsChart`: the same empty plot and month axis, without columns. */
function _temp6(month) {
  return <li className="min-w-0 flex-1" key={month.key}>{month.short}</li>;
}
export function MonthlyProjectsChartSkeleton() {
  const $ = _c(5);
  const [now] = useState(_temp7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-28 border-b" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== now) {
    t1 = lastTwelveMonths(now);
    $[1] = now;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div aria-busy="true" className="text-xs/relaxed">{t0}<MonthAxis months={t1} /></div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp7() {
  return Date.now();
}